/**
 * Extract properties from an object whose key equals the given type or is a
 * hyphen-namespaced child of it (e.g. type `data` matches `data-foo` but not
 * the camelCase `dataFoo`, which is not a valid DOM attribute).
 * @param props - The object to extract properties from
 * @param type - The key, or key namespace, to match property keys against
 * @returns An object containing only the matching properties
 */
export function extractProps<T, E extends ExtractedProp>(props: Dictionary<string, any>, type: E): ExtractedProps<T, E>;

/**
 * Extract properties from an object whose key equals, or is a hyphen-namespaced
 * child of, any of the specified types.
 * @param props - The object to extract properties from
 * @param types - Array of keys, or key namespaces, to match property keys against
 * @returns An object containing only the matching properties
 */
export function extractProps<T, E extends ExtractedProp>(
    props: Dictionary<string, any>,
    types: E[],
): ExtractedProps<T, E>;

export function extractProps<T, E extends ExtractedProp>(
    props: Dictionary<string, any>,
    typeOrTypes: E | E[],
): ExtractedProps<T, E> {
    const types = Array.isArray(typeOrTypes) ? typeOrTypes : [typeOrTypes];
    return Object.keys(props).reduce(
        (acc, key) => {
            if (types.some(type => key === type || key.startsWith(`${type}-`))) {
                acc[key as keyof ExtractedProps<T, E>] = props[key];
            }
            return acc;
        },
        {} as ExtractedProps<T, E>,
    );
}
